/**
 * ─────────────────────────────────────────────────────────────────────────
 *  CANONICAL DOMAIN CONTRACT  ·  Crew Crashpad Management Platform
 * ─────────────────────────────────────────────────────────────────────────
 *  Verbatim mirror of backend/src/common/domain.ts. The API owns this
 *  contract; this copy lets the React app share the same roles, bed codes,
 *  and statuses. When the backend file changes, copy it across — keep the two
 *  in sync. (Separate projects, so this is a manual contract, not a package.)
 * ─────────────────────────────────────────────────────────────────────────
 */

// ─── Roles ──────────────────────────────────────────────────────────────
export enum Role {
  RENTER = 'RENTER',
  ADMIN = 'ADMIN',
  /** Housekeeping staff (e.g. Vanda) — sees only door codes + the bed-wash queue. */
  CLEANER = 'CLEANER',
}

// ─── Beds ───────────────────────────────────────────────────────────────
export enum BedType {
  TWIN = 'TWIN',
  FULL = 'FULL',
  QUEEN = 'QUEEN',
}

export enum BedStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
}

/**
 * The fixed bed inventory from the proposal. Labels are the physical bed
 * codes used on-site (number + type letter). This list seeds Module 4.
 */
export interface BedDefinition {
  code: string
  type: BedType
  roomSlug: string
  level: string
  pricePerMonth: number
}

export const BED_INVENTORY: readonly BedDefinition[] = [
  {code: '1T', type: BedType.TWIN, roomSlug: 'bunk-room-1', level: 'Bunk 1 · Top', pricePerMonth: 350},
  {code: '2F', type: BedType.FULL, roomSlug: 'bunk-room-1', level: 'Bunk 1 · Bottom', pricePerMonth: 375},
  {code: '3T', type: BedType.TWIN, roomSlug: 'bunk-room-1', level: 'Bunk 2 · Top', pricePerMonth: 350},
  {code: '4F', type: BedType.FULL, roomSlug: 'bunk-room-1', level: 'Bunk 2 · Bottom', pricePerMonth: 375},
  {code: '5Q', type: BedType.QUEEN, roomSlug: 'bunk-room-2', level: 'Bunk 3 · Top', pricePerMonth: 415},
  {code: '6T', type: BedType.TWIN, roomSlug: 'bunk-room-2', level: 'Bunk 3 · Bottom', pricePerMonth: 350},
  {code: '7F', type: BedType.FULL, roomSlug: 'bunk-room-2', level: 'Bunk 4 · Top', pricePerMonth: 375},
  {code: '8Q', type: BedType.QUEEN, roomSlug: 'bunk-room-2', level: 'Bunk 4 · Bottom', pricePerMonth: 415},
] as const

// ─── Lifecycle statuses ─────────────────────────────────────────────────

/** Module 3 — renter application & verification review queue. */
export enum ApplicationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/** Module 2 — renter account state. */
export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

/** Module 5 — booking request & waitlist. */
export enum BookingStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WAITLISTED = 'WAITLISTED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

/** Module 6 — payments & receipts. */
export enum PaymentStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  REFUNDED = 'REFUNDED',
}

export enum PaymentMethod {
  ZELLE = 'ZELLE',
  CASH = 'CASH',
  CARD = 'CARD',
  OTHER = 'OTHER',
}

/** Module 7 — cleaning & maintenance requests. */
export enum RequestType {
  CLEANING = 'CLEANING',
  MAINTENANCE = 'MAINTENANCE',
  SUGGESTION = 'SUGGESTION',
}

export enum RequestStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

/** S2 — notifications delivery channels. */
export enum NotificationChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

/** Bed-wash / linen-change jobs (housekeeping queue). */
export enum BedWashStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

export enum BedWashSource {
  /** The renter asked for a wash (and paid the $5). */
  REQUESTED = 'REQUESTED',
  /** Auto-added by the 6-week hygiene rule (renter's card charged $5). */
  AUTO = 'AUTO',
}

/** Cleaner earnings ledger — what the business owes the cleaner. */
export enum CleanerEarningKind {
  BED_WASH = 'BED_WASH',
  CLEANING_VISIT = 'CLEANING_VISIT',
}

export enum CleanerEarningStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  OWED = 'OWED',
  PAID = 'PAID',
  DECLINED = 'DECLINED',
}

export enum CleanerPayoutMethod {
  STRIPE = 'STRIPE',
  MANUAL = 'MANUAL',
}

export enum CleanerPayoutStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  FAILED = 'FAILED',
}

/** Whether the cleaner has finished Stripe Connect onboarding. */
export enum CleanerConnectStatus {
  NONE = 'NONE',
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
}
